import { Skeleton } from "@mantine/core";

import { useOpenCreatorPlusModal } from "_common/hooks/useOpenCreatorPlusModal";
import Toast                       from "_common/toast";
import { PrimaryButton }           from "_components/atoms/button";
import FetchMore                   from "_components/atoms/fetchMoreLoader";
import Icon                        from "_components/atoms/icon";
import Image                       from "_components/atoms/image";
import Typography                  from "_components/atoms/typography";
import { deleteLivePost }          from "_services/api/broadcast";

import useStyles from "./styles";

const StreamsToBeDeleted = ( {
	deletionTime,
	fetchMore,
	isDepleted,
	isLoading,
	isLoadingMore,
	onClose,
	onDeleteStream,
	streams = [],
	totalCount = 0
} ) => {
	const { classes }          = useStyles ();
	const openCreatorPlusModal = useOpenCreatorPlusModal ();

	const handleDeleteStream = async streamId => {
		try {
			const res = await deleteLivePost ( streamId );

			if ( res?.deleteSession ) {
				if ( onDeleteStream ) {
					onDeleteStream ( streamId );
				}
			}
		} catch ( err ) {
			Toast.error ( err.displayMsg );
		}
	};

	// Calculate days until deletion
	const getDaysUntilDeletion = () => {
		if ( !deletionTime )
			return 7; // Default to 7 days if deletionTime is not available

		const deletionDate = new Date ( deletionTime );
		const now          = new Date ();
		const diffTime     = deletionDate - now;
		const diffDays     = Math.ceil ( diffTime / ( 1000 * 60 * 60 * 24 ) );

		return diffDays > 0 ? diffDays : 0;
	};

	const daysUntilDeletion = getDaysUntilDeletion ();

	const formatDate = dateString => {
		if ( !dateString )
			return "";

		const date     = new Date ( dateString );
		const now      = new Date ();
		const diffTime = Math.abs ( now - date );
		const diffDays = Math.ceil ( diffTime / ( 1000 * 60 * 60 * 24 ) );

		if ( diffDays < 30 )
			return `${ diffDays } days ago`;
		if ( diffDays < 365 )
			return `${ Math.floor ( diffDays / 30 ) } months ago`;

		return `${ Math.floor ( diffDays / 365 ) } years ago`;
	};

	const formatViews = views => {
		if ( !views )
			return "0 views";

		if ( views >= 1000000 )
			return `${ ( views / 1000000 ).toFixed ( 1 ) }M views`;

		if ( views >= 1000 )
			return `${ ( views / 1000 ).toFixed ( 1 ) }K views`;

		return `${ views } views`;
	};

	return (
		<div className = { classes.modalWrapper }>
			<div className = { classes.header }>
				<Typography className     = { classes.title }
					title                    = "Streams to be deleted"
				/>

				<Icon className     = { classes.closeIcon }
					name               = "close"
					onClick            = { onClose }
				/>
			</div>

			<div className = { classes.descriptionBlock }>
				<Typography
					className = { classes.heading }
					title     = { `${ totalCount || streams.length } Streams Deleting in ${ daysUntilDeletion } Days` }
				/>

				<Typography
					className = { classes.subtext }
					title     = "Save your stream by upgrading to Creator Plus"
				/>

				<PrimaryButton
					className = { classes.upgradeBtn }
					onClick   = { openCreatorPlusModal }
					title     = { "Upgrade to Creator Plus" }
				/>
			</div>

			<div className = { classes.streamList }>
				{isLoading ? (
					// Skeleton loader
					Array.from ( { length: 5 } ).map ( ( _, index ) => (
						<div key      = { index }
							className    = { classes.streamCard }
						>
							<Skeleton
								className = { classes.thumbnail }
								height    = "45px"
								radius    = "8px"
								width     = "60px"
							/>

							<div className = { classes.streamText }>
								<Skeleton
									height = "16px"
									mb     = "8px"
									width  = "80%"
								/>

								<Skeleton
									height = "14px"
									width  = "60%"
								/>
							</div>

							<Skeleton
								circle
								height = "20px"
								width  = "20px"
							/>
						</div>
					) )
				) : streams.length > 0 ? (
					streams.map ( ( stream, index ) => (
						<div key      = { index }
							className    = { classes.streamCard }
						>
							<Image
								alt       = "Stream thumbnail"
								className = { classes.thumbnail }
								src       = { stream.thumbnail || "/images/thumbnail_placeholder.webp" }
							/>

							<div className = { classes.streamContentWrapper }>
								<div className = { classes.titleRow }>
									<Typography className     = { classes.streamTitle }
										title                    = { `${ stream.title || "Untitled Stream" }` }
									/>

									<div className = { classes.moreIconWrapper }>
										<Icon
											sameInBothTheme
											className = { classes.svgIcon }
											name      = "deleteFilled"
											onClick   = { () => handleDeleteStream ( stream.id ) }
											size      = "20px"
										/>
									</div>
								</div>

								<Typography
									className = { classes.streamMeta }
									title     = { `${ formatDate ( stream.startedAt ) } • ${ formatViews ( stream.viewsCount ) }` }
								/>
							</div>
						</div>
					) )
				) : (
					<div className = { classes.emptyState }>
						<Typography
							className = { classes.emptyText }
							title     = "No streams to be deleted"
						/>
					</div>
				)}

				{streams.length > 0 && (
					<FetchMore
						fetchMore  = { fetchMore }
						isDepleted = { isDepleted }
						isLoading  = { isLoadingMore }
					/>
				)}
			</div>
		</div>
	);
};

export default StreamsToBeDeleted;
